import { PlayIcon as PlaySolidIcon } from '@heroicons/react/24/solid'
import { Button } from '@duck/ui'
import { useEffect, useRef, useState, type ImgHTMLAttributes } from 'react'
import { useTheme } from '@/contexts/ThemeContext'
import { useTar } from '@/modules/i18n/useTar'
import { ExternalAssets } from '@/types/externalURLs'

const HERO_TAGS = ['sql', 'python', 'realDatasets'] as const

// Renders at opacity 0 and transitions to 1 on the img's load event. Also covers the cached/
// already-complete case (e.g. hydrating prerendered HTML where the image finished loading
// before the React onLoad listener was attached) by checking `complete` on mount.
const FadeInImage = ({ className, ...rest }: ImgHTMLAttributes<HTMLImageElement>) => {
  const [loaded, setLoaded] = useState(false)
  const ref = useRef<HTMLImageElement>(null)
  useEffect(() => {
    if (ref.current?.complete) setLoaded(true)
  }, [])
  return (
    <img
      ref={ref}
      {...rest}
      onLoad={() => setLoaded(true)}
      className={`transition-opacity duration-700 ${loaded ? 'opacity-100' : 'opacity-0'} ${className ?? ''}`}
    />
  )
}

const getHeroArtTransform = (horizontalOffset = 0, verticalOffset = 0, scale = 1, isRtl = false) => {
  const rtlSign = isRtl ? -1 : 1
  return `scale(${scale}) rotateY(${(-20 + horizontalOffset * 5) * rtlSign}deg) rotateX(${4 - verticalOffset * 3}deg)`
}

interface HomePageHeroProps {
  isAuthenticated: boolean
  onPrimaryCta: () => void
  onLoginCta: () => void
}

export const HomePageHero = ({
  isAuthenticated,
  onPrimaryCta,
  onLoginCta,
}: HomePageHeroProps) => {
  const { t, isRtl } = useTar()
  const { isDark } = useTheme()
  const heroArtRef = useRef<HTMLDivElement>(null)
  const heroArtPointerOffsetRef = useRef({ horizontal: 0, vertical: 0 })
  const heroArtScaleRef = useRef(1)
  const mobileHeroImageRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updateHeroArtTransform = () => {
      if (!heroArtRef.current) {
        return
      }

      const { horizontal, vertical } = heroArtPointerOffsetRef.current
      heroArtRef.current.style.transform = getHeroArtTransform(
        horizontal,
        vertical,
        heroArtScaleRef.current,
        isRtl
      )
    }

    const resetHeroArtTransform = () => {
      if (!heroArtRef.current) {
        return
      }

      heroArtPointerOffsetRef.current = { horizontal: 0, vertical: 0 }
      updateHeroArtTransform()
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (!heroArtRef.current || window.innerWidth === 0 || window.innerHeight === 0) {
        return
      }

      const horizontalOffset = (event.clientX / window.innerWidth - 0.5) * 2
      const verticalOffset = (event.clientY / window.innerHeight - 0.5) * 2

      heroArtPointerOffsetRef.current = {
        horizontal: horizontalOffset,
        vertical: verticalOffset,
      }
      updateHeroArtTransform()
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('blur', resetHeroArtTransform)
    document.documentElement.addEventListener('pointerleave', resetHeroArtTransform)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('blur', resetHeroArtTransform)
      document.documentElement.removeEventListener('pointerleave', resetHeroArtTransform)
    }
  }, [isRtl])

  useEffect(() => {
    const TRANSLATE_START_PERCENT = -30
    const TRANSLATE_END_PERCENT = 0
    const TRANSLATE_START_OFFSET_PX = -40
    let rafId = 0

    const updateMobileHeroImageOffset = () => {
      rafId = 0
      const imageElement = mobileHeroImageRef.current
      if (!imageElement) {
        return
      }

      const rect = imageElement.getBoundingClientRect()
      if (rect.width === 0) {
        return
      }

      const wrapperWidth = imageElement.parentElement?.getBoundingClientRect().width ?? rect.width
      const imageWidth = rect.width

      if (imageWidth <= wrapperWidth) {
        imageElement.style.transform = 'translateX(0%)'
        return
      }

      const viewportHeight = window.innerHeight
      const totalDistance = viewportHeight + rect.height
      const traveled = viewportHeight - rect.top
      const progress = totalDistance > 0 ? Math.max(0, Math.min(1, traveled / totalDistance)) : 0

      const rtlOffset = (isRtl ? 25 : 0)
      const translatePercent =
        TRANSLATE_START_PERCENT +
        progress * (TRANSLATE_END_PERCENT - TRANSLATE_START_PERCENT) +
        rtlOffset

      const offsetPx = (1 - progress) * TRANSLATE_START_OFFSET_PX
      imageElement.style.transform = `translateX(calc(${translatePercent}% + ${offsetPx}px))`
    }

    const scheduleUpdate = () => {
      if (rafId) {
        return
      }
      rafId = window.requestAnimationFrame(updateMobileHeroImageOffset)
    }

    scheduleUpdate()
    window.addEventListener('scroll', scheduleUpdate, { passive: true, capture: true })
    window.addEventListener('resize', scheduleUpdate)

    // 'load' doesn't fire on the wrapper div — attach to each <img> inside so the offset
    // recalculates once the visible variant finishes loading.
    const imgs = mobileHeroImageRef.current?.querySelectorAll('img') ?? []
    imgs.forEach((img) => img.addEventListener('load', scheduleUpdate))

    return () => {
      window.removeEventListener('scroll', scheduleUpdate, { capture: true } as EventListenerOptions)
      window.removeEventListener('resize', scheduleUpdate)
      imgs.forEach((img) => img.removeEventListener('load', scheduleUpdate))
      if (rafId) {
        window.cancelAnimationFrame(rafId)
      }
    }
  }, [])

  const handleHeroArtPointerEnter = () => {
    if (!heroArtRef.current) {
      return
    }

    heroArtScaleRef.current = 1.04
    const { horizontal, vertical } = heroArtPointerOffsetRef.current
    heroArtRef.current.style.transform = getHeroArtTransform(horizontal, vertical, heroArtScaleRef.current, isRtl)
  }

  const handleHeroArtPointerLeave = () => {
    if (!heroArtRef.current) {
      return
    }

    heroArtScaleRef.current = 1
    const { horizontal, vertical } = heroArtPointerOffsetRef.current
    heroArtRef.current.style.transform = getHeroArtTransform(horizontal, vertical, heroArtScaleRef.current, isRtl)
  }

  return (
    <section className="relative isolate" aria-labelledby="hero-heading">
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-x-24 -inset-y-16 -z-20"
        style={{
          backgroundImage: `linear-gradient(to right, hsl(var(--color-accent) / ${
            isDark ? 0.14 : 0.38
          }) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--color-accent) / ${
            isDark ? 0.14 : 0.38
          }) 1px, transparent 1px)`,
          backgroundSize: '56px 56px',
          WebkitMaskImage:
            'radial-gradient(ellipse 60% 65% at 50% 50%, black 25%, transparent 90%)',
          maskImage:
            'radial-gradient(ellipse 60% 65% at 50% 50%, black 25%, transparent 90%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-x-24 -inset-y-20 -z-10"
        style={{
          background:
            'radial-gradient(ellipse 47% 45% at 70% 50%, hsl(var(--color-accent) / 0.28), transparent 65%)',
        }}
      />

      <div className="relative grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] items-center gap-10 px-4 py-10 md:px-10 md:py-16 lg:px-14 lg:py-20">
        <div className="flex flex-col items-start gap-6 text-start max-w-lg">
          <h1
            id="hero-heading"
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-foreground text-balance"
          >
            {t('home.heroTitlePrefix')}{' '}
            <span className="text-accent">{t('home.heroTitleEmphasis')}.</span>
          </h1>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            {t('home.heroSubtitle')}
          </p>

          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 pt-1 w-full sm:w-auto">
            <Button
              onClick={onPrimaryCta}
              size="lg"
              className="group bg-accent hover:bg-accent/85 text-accent-foreground text-base font-semibold px-5 py-5 rounded-lg shadow-[0_0_40px_-8px_hsl(var(--color-accent)/0.55)] transition-all hover:-translate-y-0.5 w-full sm:w-auto"
            >
              {isAuthenticated
                ? t('home.heroTryChallenge')
                : t('home.heroGetStarted')}
              <span
                aria-hidden
                className="ms-2 inline-block transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 rtl:rotate-180"
              >
                →
              </span>
            </Button>
            {!isAuthenticated && (
              <Button
                variant="outline"
                size="lg"
                onClick={onLoginCta}
                className="bg-card/70 border-border text-foreground hover:bg-card text-base font-semibold px-5 py-5 rounded-lg backdrop-blur-sm transition-all hover:-translate-y-0.5 w-full sm:w-auto"
              >
                {t('home.heroLogin')}
              </Button>
            )}
          </div>

          <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">
            {HERO_TAGS.map((tag) => (
              <li key={tag} className="flex items-center gap-1.5">
                <PlaySolidIcon
                  aria-hidden
                  className="h-2.5 w-2.5 rtl:rotate-180"
                />
                {t(`home.heroTags.${tag}`)}
              </li>
            ))}
          </ul>

        </div>

        <div
          aria-hidden
          className="relative hidden sm:block md:-me-10 lg:me-0"
          onPointerEnter={handleHeroArtPointerEnter}
          onPointerLeave={handleHeroArtPointerLeave}
        >
          <div className="relative" style={{ perspective: '1100px' }}>
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-24 -z-10"
              style={{
                background:
                  'radial-gradient(ellipse 45% 50% at 50% 50%, hsl(var(--color-accent) / 0.55), transparent 70%)',
                filter: 'blur(40px)',
              }}
            />
            <div
              ref={heroArtRef}
              dir="ltr"
              className="relative"
              style={{
                transform: getHeroArtTransform(0, 0, 1, isRtl),
                transformOrigin: 'center center',
                transformStyle: 'preserve-3d',
                transition: 'transform 160ms ease-out',
                willChange: 'transform',
              }}
            >
              {/*<span className="absolute -top-3 -start-3 h-10 w-10 border-t-[1.5px] border-s-[1.5px] border-accent" />*/}
              {/*<span className="absolute -top-3 -end-3 h-10 w-10 border-t-[1.5px] border-e-[1.5px] border-accent" />*/}
              {/*<span className="absolute -bottom-3 -start-3 h-10 w-10 border-b-[1.5px] border-s-[1.5px] border-accent" />*/}
              {/*<span className="absolute -bottom-3 -end-3 h-10 w-10 border-b-[1.5px] border-e-[1.5px] border-accent" />*/}

              {/* Render both variants and let CSS pick — the prerendered HTML bakes in one src, so
                  reading `isDark` from React state would flash the wrong image on dark-mode hydration. */}
              <FadeInImage
                src={ExternalAssets.WorkspaceScreenshotLight}
                alt=""
                loading="eager"
                fetchPriority="high"
                className="block dark:hidden w-full max-w-[450px] mx-auto md:max-w-none md:mx-0 rounded-md ring-1 ring-border/70 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.7)]"
              />
              <FadeInImage
                src={ExternalAssets.WorkspaceScreenshotDark}
                alt=""
                loading="eager"
                fetchPriority="high"
                className="hidden dark:block w-full max-w-[450px] mx-auto md:max-w-none md:mx-0 rounded-md ring-1 ring-border/70 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.7)]"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        aria-hidden
        className="sm:hidden relative left-1/2 -translate-x-1/2 w-screen max-w-[450px] overflow-hidden mt-4"
      >
        {/* Wrapper carries the scroll-driven transform so both theme variants can share it —
            rendering only one variant via `isDark` flashes the wrong image on prerendered HTML.
            min-h reserves the rendered height while the image is loading so the page below
            doesn't shift up and then back down when the FadeInImage fades in. */}
        <div
          ref={mobileHeroImageRef}
          className="block w-[130%] max-w-none min-h-[295px]"
          style={{
            transform: 'translateX(calc(-30% - 40px))',
            willChange: 'transform',
          }}
        >
          <FadeInImage
            src={ExternalAssets.WorkspaceScreenshotLight}
            alt=""
            loading="eager"
            fetchPriority="high"
            className="block dark:hidden w-full"
          />
          <FadeInImage
            src={ExternalAssets.WorkspaceScreenshotDark}
            alt=""
            loading="eager"
            fetchPriority="high"
            className="hidden dark:block w-full"
          />
        </div>
      </div>
    </section>
  )
}
